import React, { useState, useEffect } from "react";
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, TextField, Autocomplete, Chip, Tooltip, IconButton, Dialog, 
  DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Snackbar, 
  Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import { useApiContext } from "../../../Utils/ApiContext/ApiContext";

const AllExercises = ({onExerciseClick}) => {
  const accessToken = getDecryptedAccessToken();
  const { exercises, fetchData } = useApiContext();

//   const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPrimaryMuscles, setSelectedPrimaryMuscles] = useState([]);
  const [selectedSecondaryMuscles, setSelectedSecondaryMuscles] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [primaryMuscleOptions, setPrimaryMuscleOptions] = useState([]);
  const [secondaryMuscleOptions, setSecondaryMuscleOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const fetchExercises=()=>{
    fetchData("/apis/default/api/exercises");
  }



  useEffect(() => {
    fetchExercises();
    setFilteredExercises(exercises);
        extractFilterOptions(exercises);
  }, [exercises]);

//   const fetchExercises = () => {
//     setLoading(true);
//     ApiServices.get("/apis/default/api/exercises", {
//       Authorization: `Bearer ${accessToken}`,
//     })
//       .then((response) => {
//         const data = response.data;
//         setExercises(data);
        // setFilteredExercises(data);
        // extractFilterOptions(data);
//         setLoading(false);
//       })
//       .catch((error) => {
//         console.error("Error fetching exercises:", error);
//         setLoading(false);
//       });
//   };

  const extractFilterOptions = (data) => {
    const primaryMusclesSet = new Set();
    const secondaryMusclesSet = new Set();
    const equipmentSet = new Set();
    
    data.forEach((exercise) => {
      exercise.primary_muscles.replace(/"/g, "").split(", ").forEach(muscle => primaryMusclesSet.add(muscle));
      exercise.secondary_muscles.replace(/"/g, "").split(", ").forEach(muscle => secondaryMusclesSet.add(muscle));
      equipmentSet.add(exercise.equipment.replace(/"/g, ""));
    });

    setPrimaryMuscleOptions(Array.from(primaryMusclesSet));
    setSecondaryMuscleOptions(Array.from(secondaryMusclesSet));
    setEquipmentOptions(Array.from(equipmentSet));
  };

  const applyFilters = (search, primaryMuscles, secondaryMuscles, equipment) => {
    let filtered = exercises;

    if (search) {
      filtered = filtered.filter(ex => 
        ex.exercise_name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (primaryMuscles.length > 0) {
      filtered = filtered.filter(ex =>
        primaryMuscles.some(muscle => ex.primary_muscles.includes(muscle))
      );
    }

    if (secondaryMuscles.length > 0) {
      filtered = filtered.filter(ex =>
        secondaryMuscles.some(muscle => ex.secondary_muscles.includes(muscle))
      );
    }

    if (equipment.length > 0) {
      filtered = filtered.filter(ex => equipment.includes(ex.equipment));
    }

    setFilteredExercises(filtered);
  };

  const handleDeleteClick = (exercise) => {
    setExerciseToDelete(exercise);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    ApiServices.delete(`/apis/default/api/exercises/${exerciseToDelete.id}`, {
      Authorization: `Bearer ${accessToken}`,
    })
      .then(() => {
        setSnackbar({ open: true, message: "Exercise deleted successfully!", severity: "success" });
        fetchExercises();
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Failed to delete exercise!", severity: "error" });
      })
      .finally(() => {
        setDeleteDialogOpen(false);
      });
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <TextField
          label="Search Exercise"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            applyFilters(e.target.value, selectedPrimaryMuscles, selectedSecondaryMuscles, selectedEquipment);
          }}
          fullWidth
        />
        <Autocomplete
          multiple
          options={primaryMuscleOptions}
          getOptionLabel={(option) => option}
          value={selectedPrimaryMuscles}
          onChange={(e, newValue) => {
            setSelectedPrimaryMuscles(newValue);
            applyFilters(searchQuery, newValue, selectedSecondaryMuscles, selectedEquipment);
          }}
          renderInput={(params) => <TextField {...params} label="Primary Muscles" variant="outlined" size="small" />}
          fullWidth
        />
        <Autocomplete
          multiple
          options={secondaryMuscleOptions}
          getOptionLabel={(option) => option}
          value={selectedSecondaryMuscles}
          onChange={(e, newValue) => {
            setSelectedSecondaryMuscles(newValue);
            applyFilters(searchQuery, selectedPrimaryMuscles, newValue, selectedEquipment);
          }}
          renderInput={(params) => <TextField {...params} label="Secondary Muscles" variant="outlined" size="small" />}
          fullWidth
        />
        <Autocomplete
          multiple
          options={equipmentOptions}
          getOptionLabel={(option) => option}
          value={selectedEquipment}
          onChange={(e, newValue) => {
            setSelectedEquipment(newValue);
            applyFilters(searchQuery, selectedPrimaryMuscles, selectedSecondaryMuscles, newValue);
          }}
          renderInput={(params) => <TextField {...params} label="Equipment" variant="outlined" size="small" />}
          fullWidth
        />
      </div>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Exercise Name</b></TableCell>
              <TableCell><b>Primary Muscles</b></TableCell>
              <TableCell><b>Secondary Muscles</b></TableCell>
              <TableCell><b>Equipment</b></TableCell>
              <TableCell><b>Sets & Reps</b></TableCell>
              <TableCell><b>GIF</b></TableCell>
              <TableCell><b>Delete</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredExercises?.map((exercise) => (
              <TableRow key={exercise.id} onClick={(e) => e.stopPropagation() || onExerciseClick(exercise)}>
                <TableCell>{exercise.exercise_name}</TableCell>
                <TableCell>{exercise.primary_muscles.replace(/"/g, "")}</TableCell>
                <TableCell>{exercise.secondary_muscles.replace(/"/g, "")}</TableCell>
                <TableCell>{exercise.equipment.replace(/"/g, "")}</TableCell>
                <TableCell>{`${JSON.parse(exercise.sets)[0]} ${JSON.parse(exercise.units)[0]}, ${JSON.parse(exercise.sets)[1]} ${JSON.parse(exercise.units)[1]}`}</TableCell>
                <TableCell><img src={exercise.gif_url} alt="Exercise GIF" width="50" height="50" /></TableCell>
                <TableCell>
                    <IconButton  onClick={(e) => {
            e.stopPropagation(); // Prevent triggering row click event
            handleDeleteClick(exercise);
          }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete {exerciseToDelete?.exercise_name}?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default AllExercises;
